import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import {
  ErrorLogId,
  OnAllocate,
  OnDeallocate,
  RegistrationMain,
  StatusModal,
  usePrevious,
} from "@timeedit/registration-components";
import {
  LoadRegistrationExpandBody,
  LoadRegistrationExpandResult,
  SaveStudentsResult,
} from "@timeedit/registration-schemas";
import {
  ErrorTexts,
  RegistrationError,
  getKnownErrorTexts,
  isDefined,
} from "@timeedit/registration-shared";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import {
  useDeallocateMutation,
  useFetchRegistrationQuery,
  useJoinWaitlistMutation,
  useLazyFetchExpandQuery,
  useLeaveWaitlistMutation,
  useSwapMutation,
} from "../../redux/api/registration";
import { useAppSelector, useMapping } from "../../redux/hooks";
import { useAnalytics } from "../../utils/useAnalytics";
import { useNavigateWithSignature } from "../useNavigateWithSignature";
import { useMutationSubscriptionRef } from "../../hooks/useMutationSubscriptionRef";
import { useIsHub } from "../RootPage/HubLayoutContext";
import { Spin } from "@timeedit/ui-components";

export function RegistrationPage(): React.JSX.Element {
  const mapping = useMapping();
  const signal = useAppSelector((state) => state.computed.signal);
  const isHub = useIsHub();
  const navigateWithSignature = useNavigateWithSignature();
  const params = useParams();

  const [triggerExpandQuery] = useLazyFetchExpandQuery();

  const allocateRef = useMutationSubscriptionRef();
  const [swap, swapResult] = useSwapMutation();

  const deallocateRef = useMutationSubscriptionRef();
  const [deallocate, deallocateResult] = useDeallocateMutation();

  const joinWaitlistRef = useMutationSubscriptionRef();
  const [joinWaitlist, joinWaitlistResult] = useJoinWaitlistMutation();

  const leaveWaitlistRef = useMutationSubscriptionRef();
  const [leaveWaitlist, leaveWaitlistResult] = useLeaveWaitlistMutation();

  const registration = useFetchRegistrationQuery();

  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorModalTexts, setErrorModalTexts] = useState<ErrorTexts>({
    text: "",
    title: "",
    knownError: false,
    logId: "",
  });

  useHandleAllocationResult({
    setErrorModalTexts,
    setOpenErrorModal,
    deallocateIsError: deallocateResult.isError,
    deallocateError: deallocateResult.error,
    swapIsError: swapResult.isError,
    swapError: swapResult.error,
    swapSuccess: swapResult.isSuccess,
    swapData: swapResult.data,
  });

  const [currentLoadingGroup, setCurrentLoadingGroup] = useState(0);

  const isLoading =
    registration.isLoading ||
    registration.isFetching ||
    swapResult.isLoading ||
    deallocateResult.isLoading ||
    joinWaitlistResult.isLoading ||
    leaveWaitlistResult.isLoading;

  const prevIsLoading = usePrevious(isLoading);

  const { analyticsEvent } = useAnalytics();

  useEffect(() => {
    if (!isLoading && prevIsLoading) {
      setCurrentLoadingGroup(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const handleExpand = useCallback(
    async (
      body: LoadRegistrationExpandBody
    ): Promise<LoadRegistrationExpandResult> => {
      return await triggerExpandQuery(body, true).unwrap();
    },
    [triggerExpandQuery]
  );

  const handleAllocate = useCallback(
    ({ groupId, newObjectId }: OnAllocate): void => {
      if (!isDefined(registration.data)) return;
      setCurrentLoadingGroup(groupId);
      allocateRef.current = swap({
        studentId: registration.data.studentId,
        newObjectId,
      });
    },
    [registration.data, swap, allocateRef]
  );

  const handleDeallocate = useCallback(
    ({ groupId, objectIds }: OnDeallocate): void => {
      if (!isDefined(registration.data)) return;
      setCurrentLoadingGroup(groupId);
      const studentInfo = { [registration.data.studentId]: objectIds };
      deallocateRef.current = deallocate({ studentInfo });
    },
    [registration.data, deallocate, deallocateRef]
  );

  const handleJoinWaitlist = useCallback(
    (groupId: number): void => {
      if (!isDefined(registration.data)) return;
      setCurrentLoadingGroup(groupId);
      joinWaitlistRef.current = joinWaitlist({
        groupId,
        studentId: registration.data.studentId,
        trigger: "student",
      });
    },
    [registration.data, joinWaitlist, joinWaitlistRef]
  );

  const handleLeaveWaitlist = useCallback(
    (groupId: number): void => {
      if (!isDefined(registration.data)) return;
      setCurrentLoadingGroup(groupId);
      leaveWaitlistRef.current = leaveWaitlist({
        groupId,
        studentId: registration.data.studentId,
      });
    },
    [registration.data, leaveWaitlist, leaveWaitlistRef]
  );

  const allocationControlActions = useMemo(
    () => ({
      onAllocate: handleAllocate,
      onDeallocate: handleDeallocate,
      currentLoadingGroup,
      onJoinWaitlist: handleJoinWaitlist,
      onLeaveWaitlist: handleLeaveWaitlist,
    }),
    [
      handleAllocate,
      handleDeallocate,
      currentLoadingGroup,
      handleJoinWaitlist,
      handleLeaveWaitlist,
    ]
  );

  const loadActions = useMemo(
    () => ({
      loadRegistrationExpand: handleExpand,
    }),
    [handleExpand]
  );

  if (registration.isError) {
    throw new RegistrationError({
      errors: [registration.error],
    });
  }

  if (registration.isLoading || !registration.data) {
    return <Spin center />;
  }

  return (
    <>
      <RegistrationMain
        params={params}
        signal={signal}
        isHub={isHub}
        allocationControlActions={allocationControlActions}
        navigate={navigateWithSignature}
        mapping={mapping}
        registration={registration.data}
        loadActions={loadActions}
        analyticsEvent={analyticsEvent}
      />

      <StatusModal
        open={openErrorModal}
        title={errorModalTexts.title}
        cancelButtonProps={{ style: { display: "none" } }}
        closable={false}
        onOk={() => {
          setOpenErrorModal(false);
        }}
        text={
          <StatusModalText
            text={errorModalTexts.text}
            logId={errorModalTexts.logId}
          />
        }
        severity={errorModalTexts.knownError ? "Info" : "Error"}
      />
    </>
  );
}

export type UseHandleAllocationResultProps = {
  swapSuccess: boolean;
  swapError: FetchBaseQueryError | SerializedError | undefined;
  swapData: SaveStudentsResult | undefined;
  swapIsError: boolean;
  deallocateError: FetchBaseQueryError | SerializedError | undefined;
  deallocateIsError: boolean;
  setOpenErrorModal: Dispatch<SetStateAction<boolean>>;
  setErrorModalTexts: Dispatch<SetStateAction<ErrorTexts>>;
};
export function useHandleAllocationResult({
  setErrorModalTexts,
  setOpenErrorModal,
  deallocateIsError,
  swapData,
  deallocateError,
  swapError,
  swapIsError,
  swapSuccess,
}: UseHandleAllocationResultProps): void {
  useEffect(() => {
    if (swapSuccess && swapData?.failedGroups) {
      const hasErrors = Object.values(swapData.failedGroups).some(
        (group) =>
          group?.some(
            (error) =>
              isDefined(error.reasons) &&
              (isDefined(error.reasons.failsWithMessage) ||
                isDefined(error.reasons.failsWithGroups))
          )
      );

      if (hasErrors) {
        setErrorModalTexts(
          getKnownErrorTexts({
            kind: "failedGroups",
            failedGroups: swapData.failedGroups,
            logId: swapData.logId ?? "",
          })
        );

        setOpenErrorModal(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swapData?.failedGroups, swapData?.logId, swapSuccess]);

  useHandleAllocateError({
    error: swapError,
    isError: swapIsError,
    setErrorModalTexts,
    setOpenErrorModal,
  });
  useHandleAllocateError({
    error: deallocateError,
    isError: deallocateIsError,
    setErrorModalTexts,
    setOpenErrorModal,
  });
}

type HandleAllocateErrorProps = {
  error: FetchBaseQueryError | SerializedError | undefined;
  isError: boolean;
} & Pick<
  UseHandleAllocationResultProps,
  "setOpenErrorModal" | "setErrorModalTexts"
>;
function useHandleAllocateError({
  isError,
  setOpenErrorModal,
  setErrorModalTexts,
  error,
}: HandleAllocateErrorProps): void {
  useEffect(() => {
    if (isError && isDefined(error)) {
      setErrorModalTexts(
        getKnownErrorTexts({
          kind: "error",
          error,
        })
      );

      setOpenErrorModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isError]);
}

type StatusModalTextProps = {
  text: string;
  logId: string | string[] | undefined;
};
function StatusModalText({
  text,
  logId,
}: StatusModalTextProps): React.JSX.Element {
  return (
    <>
      <p>{text}</p>
      <br />
      <ErrorLogId error={{ logId }} />
    </>
  );
}
